/*
 * pcard-title-fit.js
 *
 * Equalizes the height of every `.product-card__title.text-pcard-title`
 * element within a section so short (1-line) and long (2-line) product titles
 * line up — keeping the title / BV rating / price rows aligned across cards.
 * The reset step also neutralizes the parent `.product-card__title-row`'s
 * Tailwind min-height floor (tw-min-h-[48px] / md:tw-min-h-[60px]) so a group
 * whose titles all fit on one line collapses to one line rather than always
 * reserving 2-line space.
 *
 * Scope is per-section: each section opts in by adding `data-pcard-title-fit`
 * to the element that wraps its product-card list (the slider carousel or the
 * grid). Multiple sections on one page are handled independently — the max
 * height is computed within each root, not across the page.
 *
 * Used by:
 *  - sections/related-products-v2.liquid (grid OR drag slider)
 *  - sections/product-tabs-v2.liquid     (drag slider, with switchable tabs)
 *
 * Hidden cards measure 0 height (a hidden tab panel, or a card in a collapsed
 * template) — those are skipped so they never drag the max down to 0, and the
 * pass re-runs when they become visible (tab switch, MutationObserver).
 *
 * Re-run triggers: DOMContentLoaded, window 'load', debounced window 'resize',
 * document.fonts.ready, window 'pageshow' (bfcache restore), the theme's
 * bubbling 'tabChange' event (product-tabs tab switch), 'collection:rerendered'
 * and 'shopify:section:load' (theme editor), plus a per-root MutationObserver
 * for lazily-rendered tab templates and slider clones.
 *
 * ES5-compatible (var / function) to match the other v2 card assets.
 */
(function () {
  if (window.__pcardTitleFitInit) return;
  window.__pcardTitleFitInit = true;

  var TITLE_SELECTOR = '.product-card__title.text-pcard-title';
  var ROOT_SELECTOR = '[data-pcard-title-fit]';
  var RESIZE_DEBOUNCE_MS = 150;

  // Equalize titles within a single root element.
  function equalizeRoot(root) {
    if (!root) return;
    var titles = root.querySelectorAll(TITLE_SELECTOR);
    if (!titles.length) return; // guard: nothing to fit yet

    var i;

    // 1) Reset so wrapping is natural and resizing DOWN works (no stale heights).
    //    Also neutralize the title-row's Tailwind min-height floor
    //    (tw-min-h-[48px] / md:tw-min-h-[60px]) with an inline min-height:0 so a
    //    group whose titles all fit on one line collapses to one line instead of
    //    reserving 2-line space. Inline style beats the utility class; the class
    //    stays in the markup as the no-JS fallback.
    for (i = 0; i < titles.length; i++) {
      titles[i].style.height = 'auto';
      var titleRow = titles[i].closest ? titles[i].closest('.product-card__title-row') : titles[i].parentNode;
      if (titleRow) titleRow.style.minHeight = '0px';
    }

    // 2) Measure visible titles only; a hidden card reports offsetHeight 0.
    var max = 0;
    for (i = 0; i < titles.length; i++) {
      var h = titles[i].offsetHeight;
      if (h > max) max = h;
    }
    if (max <= 0) return; // every title hidden (collapsed tab) — try again later

    // 3) Apply the max to every visible title; leave hidden ones at 'auto' so
    //    they get equalized when they become visible.
    var maxPx = max + 'px';
    for (i = 0; i < titles.length; i++) {
      if (titles[i].offsetHeight > 0) {
        titles[i].style.height = maxPx;
      }
    }
  }

  // Equalize every root on the page (or only those inside `scope`, if given).
  function equalizeAll(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      equalizeRoot(roots[i]);
    }
    // If `scope` itself is a root (e.g. a section element with the attribute).
    if (scope && scope.nodeType === 1 && scope.matches && scope.matches(ROOT_SELECTOR)) {
      equalizeRoot(scope);
    }
  }

  // rAF wrapper so layout is settled (and fonts/images applied) before measuring.
  function scheduleAll(scope) {
    requestAnimationFrame(function () {
      equalizeAll(scope);
    });
  }

  // Async sections (related-products fetches its carousel after `load`) and late
  // layout (slider init, lazy images, font swap) settle at unpredictable times,
  // and some of those changes — notably the web-font swap that re-wraps titles —
  // do NOT trigger the MutationObserver. Run a short burst of passes so a fully
  // settled measurement is always captured. Re-applying the same max is a no-op,
  // so the extra passes are harmless once heights have converged.
  function pollEqualize() {
    var delays = [0, 250, 600, 1200, 2000, 3500, 5000];
    for (var i = 0; i < delays.length; i++) {
      setTimeout(function () {
        scheduleAll();
      }, delays[i]);
    }
  }

  // --- Debounced resize -----------------------------------------------------
  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      scheduleAll();
    }, RESIZE_DEBOUNCE_MS);
  }

  // --- Global MutationObserver ----------------------------------------------
  // A single document-level observer (instead of per-root) so we also catch
  // roots that are injected AFTER this script runs — e.g. related-products-v2
  // fetches its recommendations and replaces its container async (the carousel
  // root doesn't exist at init), and product-tabs clones a tab <template> on
  // first activation. childList+subtree only (we never observe attributes), so
  // our own style.height writes don't retrigger it — no feedback loop.
  // Debounce (not rAF): related-products injects its carousel, the slider
  // initializes, lazy images load and the font swaps over ~1s — a burst of
  // mutations. rAF would fire on the FIRST mutation, mid-burst, when titles are
  // still a transient/0 height. Debouncing fires once ~250ms AFTER the last
  // mutation, when layout has settled, so the measured max is correct.
  var observeTimer = null;
  function onMutations() {
    if (observeTimer) clearTimeout(observeTimer);
    observeTimer = setTimeout(function () {
      observeTimer = null;
      scheduleAll();
    }, 250);
  }

  function observeDocument() {
    if (typeof MutationObserver === 'undefined') return;
    if (window.__pcardTitleFitObserver) return; // bind once
    var target = document.body || document.documentElement;
    if (!target) return;
    var observer = new MutationObserver(onMutations);
    observer.observe(target, { childList: true, subtree: true });
    window.__pcardTitleFitObserver = observer;
  }

  // --- Init -----------------------------------------------------------------
  function init() {
    scheduleAll();
    observeDocument();
    pollEqualize();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Images/late layout finished — re-measure and (re)attach observers for any
  // roots that mounted after DOMContentLoaded.
  window.addEventListener('load', function () {
    scheduleAll();
    observeDocument();
    pollEqualize();
  });

  // Font load changes line wrapping → title heights change.
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(function () {
      scheduleAll();
    });
  }

  window.addEventListener('resize', onResize);

  // bfcache restore (back/forward) — layout may differ from the cached pass.
  window.addEventListener('pageshow', function () {
    scheduleAll();
  });

  // Product-tabs tab switch. TabsComponent dispatches a bubbling 'tabChange'
  // CustomEvent on setActiveTab; product-tabs lazily clones the tab's
  // <template> into the panel on first activation, then animates it in over
  // ~300ms. Re-run after that animation so the now-visible cards measure
  // non-zero. The MutationObserver also fires when the template is cloned in;
  // this timed pass covers the post-animation visible state.
  document.addEventListener('tabChange', function (event) {
    var section = event.target && event.target.closest
      ? event.target.closest('.section')
      : null;
    // Immediate pass (template may already be cloned), then a post-animation
    // pass once the panel has finished revealing.
    scheduleAll(section || undefined);
    setTimeout(function () {
      scheduleAll(section || undefined);
    }, 350);
  });

  // Theme editor / AJAX re-renders.
  document.addEventListener('collection:rerendered', function () {
    scheduleAll();
    observeDocument();
  });

  document.addEventListener('shopify:section:load', function (event) {
    scheduleAll(event.target || undefined);
    observeDocument();
  });
})();
